import { type ChangeEvent, type FC, type MouseEvent } from 'react'
import * as Sentry from '@sentry/react'
import { Form } from 'react-bootstrap'
import { type ParseResult } from 'papaparse'
import { usePapaParse } from 'react-papaparse'
import { getInviteRoleGroups } from '../InviteRoleGroups'
import { type BulkCsvRows, getBulkCsvRows, type RawBulkCsvRow } from './bulkCsvRows'

const _MAX_CSV_SIZE_IN_BYTES = 10485760 // 10 MB
// Bulk invite never offers the RFG groups, not even to RFG accounts
const inviteRoleGroups = getInviteRoleGroups({ onlyInvitable: true })

export interface BulkInviteCsvInputProps {
  onCsvParsed: (result: BulkCsvRows) => void
}

export const BulkInviteCsvInput: FC<BulkInviteCsvInputProps> = ({ onCsvParsed }) => {
  const { readString } = usePapaParse()

  const cleanupFileInput = (event: MouseEvent<HTMLInputElement>) => {
    if (event.currentTarget) {
      event.currentTarget.value = ''
    }
  }

  const handleImport = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files ? event.target.files[0] : null
    if (!file) {
      onCsvParsed({ kind: 'fileError', message: 'File is missing' })
      return
    }
    if (file.size > _MAX_CSV_SIZE_IN_BYTES) {
      onCsvParsed({ kind: 'fileError', message: `CSV file is too large, allowed size: ${(_MAX_CSV_SIZE_IN_BYTES / 1048576).toFixed(2)} MB` })
      return
    }

    let content: string
    try {
      content = await file.text()
    } catch (error) {
      Sentry.captureException(error)
      onCsvParsed({ kind: 'fileError', message: 'The CSV file could not be read' })
      return
    }

    readString<RawBulkCsvRow>(content, {
      header: true,
      skipEmptyLines: true,
      complete: (result: ParseResult<RawBulkCsvRow>) => {
        onCsvParsed(getBulkCsvRows(result, inviteRoleGroups))
      },
    })
  }

  return (
    <div>
      <label htmlFor='bulk-invite-file' className='label-medium pb-2'>
        List of users to be invited. Download <a href='/dashboard/assets/sample-bulk-invite.csv'>sample CSV</a>.
      </label>
      <Form.Control id='bulk-invite-file' type='file' onClick={cleanupFileInput} onChange={handleImport} accept='text/csv' />
    </div>
  )
}
